import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { RefreshCw } from "lucide-react";
import { GalleryCard } from "@/components/gallery/GalleryCard";
import { GalleryLightbox } from "@/components/gallery/GalleryLightbox";

/** The Gallery, G1+G2 (2026-11) — Pinterest-style masonry discovery feed of NJM Build
 * freelancers' own work (YouTube videos + Floor BTS photos). Backed by GET /v1/gallery/items
 * (cursor-paginated, gallery.read-gated — freelancers + NJM staff only, see
 * backend/modules/gallery.py). Scope of THIS wave, per the approved brief:
 *   - masonry layout, a shared lightbox for video + BTS, infinite scroll, loading/empty states.
 * Stage 2+3 follow-up (2026-11) added the like toggle and Share action below. Tag-chip/
 * search-box FILTERING and lightbox prev/next arrows remain explicitly out of scope (G3).
 */
const TYPE_TABS = [
  { key: "all", label: "All" },
  { key: "video", label: "Video" },
  { key: "bts", label: "BTS" },
];

// Same tier as kernel/permissions.py's NJM_ONLY_ROLES — every staff role, explicitly NOT
// freelancer (freelancer holds gallery.read/write for browsing+liking, never a sync control).
const STAFF_ROLES = ["founder", "partner", "pm", "coordinator", "finance", "creative"];

/** Staff-only "Sync now" + status line (2026-11 follow-up) — POST/GET /v1/gallery/sync-now|
 * sync-status, same lease the 30-minute cron uses. Renders nothing for a freelancer viewer. */
function GallerySyncPanel() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const isStaff = STAFF_ROLES.includes(user?.role);

  useEffect(() => {
    if (!isStaff) return;
    api.get("/v1/gallery/sync-status").then((r) => setStatus(r.data.status)).catch(() => {});
  }, [isStaff]);

  if (!isStaff) return null;

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/v1/gallery/sync-now");
      if (r.data.skipped) toast("A sync is already running — try again in a minute.");
      else { setStatus(r.data); toast.success("Gallery synced from the latest Floor posts + work samples."); }
    } catch {
      toast.error("Sync failed — check backend logs");
    } finally { setSyncing(false); }
  };

  return (
    <div data-testid="gallery-sync-now-panel" className="flex items-center justify-between gap-4 flex-wrap rounded-lg border border-hairline bg-paper px-4 py-3">
      <p className="text-xs text-ink-soft" data-testid="gallery-sync-status-line">
        {status ? `Last sync: ${new Date(status.last_run_at).toLocaleString()} · ${status.video_count} video(s), ${status.bts_count} BTS, ${status.deactivated} removed` : "Auto-syncs every 30 minutes."}
      </p>
      <button type="button" onClick={runSync} disabled={syncing} data-testid="gallery-sync-now-button"
        className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-1.5 text-[13px] font-medium text-white hover:bg-ink/85 disabled:opacity-50 transition-colors">
        <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}

function SkeletonCard({ ratio }) {
  return (
    <div className="break-inside-avoid mb-4 rounded-xl border border-hairline bg-paper overflow-hidden" data-testid="gallery-skeleton-card">
      <div className="w-full bg-surface2 animate-pulse" style={{ aspectRatio: ratio }} />
      <div className="px-3.5 pt-3 pb-3 flex items-center gap-2.5">
        <div className="h-7 w-7 rounded-full bg-surface2 animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-2/3 rounded bg-surface2 animate-pulse" />
          <div className="h-2 w-2/5 rounded bg-surface2 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

const SKELETON_RATIOS = ["16 / 9", "4 / 5", "1 / 1", "3 / 2", "16 / 9", "3 / 4", "16 / 9", "1 / 1"];

export default function Gallery({ deepLinkItemId, clearDeepLink }) {
  const [type, setType] = useState("all");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [lightboxItemId, setLightboxItemId] = useState(null);
  const [fallbackItem, setFallbackItem] = useState(null);
  const [deepLinkNotFound, setDeepLinkNotFound] = useState(false);
  const [pendingLikeIds, setPendingLikeIds] = useState(() => new Set());
  const pendingLikeIdsRef = useRef(new Set()); // L2: see toggleLike below
  const sentinelRef = useRef(null);

  const load = useCallback((nextType, nextCursor, append) => {
    const setBusy = append ? setLoadingMore : setLoading;
    setBusy(true);
    setError(false);
    api.get("/v1/gallery/items", { params: { type: nextType, cursor: nextCursor || undefined } })
      .then((r) => {
        setItems((prev) => (append ? [...prev, ...(r.data.items || [])] : (r.data.items || [])));
        setCursor(r.data.next_cursor || null);
      })
      .catch(() => { if (!append) setItems([]); setError(true); })
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    load(type, null, false);
  }, [type, load]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || loadingMore || !cursor) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) load(type, cursor, true);
    }, { rootMargin: "400px 0px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cursor, loading, loadingMore, type, load]);

  // Stage 3 (2026-11, share) — a shared link (?tab=gallery&item=<id>, read by
  // FreelancerPortal.jsx and handed down as `deepLinkItemId`) must open even when the item
  // isn't on whatever page has loaded so far, so this fetches it directly by id rather than
  // searching/waiting on the paginated feed — works just as well for an item on page 1 or
  // page 30. Runs once per incoming id, then hands the id back via `clearDeepLink` the same
  // "consumed once" convention every other FreelancerPortal tab's deepLink already follows.
  useEffect(() => {
    if (!deepLinkItemId) return;
    let cancelled = false;
    api.get(`/v1/gallery/items/${encodeURIComponent(deepLinkItemId)}`)
      .then((r) => { if (!cancelled) { setFallbackItem(r.data); setLightboxItemId(r.data.id); } })
      .catch(() => { if (!cancelled) setDeepLinkNotFound(true); })
      .finally(() => { if (!cancelled) clearDeepLink?.(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkItemId]);

  // Likes (Stage 2, 2026-11) — ONE source of truth for both the card and the lightbox: the
  // lightbox never holds its own copy of an item, it looks the current one up by id from
  // `items` (or `fallbackItem`, for a share-link item not on any loaded page) on every render,
  // so a like toggled from either place is immediately visible in the other with no refetch.
  const updateItemLocally = useCallback((itemId, patch) => {
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
    setFallbackItem((prev) => (prev && prev.id === itemId ? { ...prev, ...patch } : prev));
  }, []);

  const toggleLike = useCallback((item) => {
    // L2 (Cursor fix pass, 2026-11): React state updates aren't applied synchronously, so two
    // synchronous calls in the same tick (a genuine same-tick double click, or a re-entrant
    // call before the first setState commits) could both read the same stale `pendingLikeIds`
    // and both pass this guard. A ref is mutated immediately, so the second call in the same
    // tick always sees the first call's entry — `pendingLikeIds` state is kept alongside only
    // to drive the button's disabled/visual state across re-renders.
    if (pendingLikeIdsRef.current.has(item.id)) return;
    pendingLikeIdsRef.current.add(item.id);
    const prevLiked = item.liked_by_me;
    const prevCount = item.like_count || 0;
    const nextLiked = !prevLiked;
    setPendingLikeIds((prev) => new Set(prev).add(item.id));
    updateItemLocally(item.id, { liked_by_me: nextLiked, like_count: prevCount + (nextLiked ? 1 : -1) });
    const encodedId = encodeURIComponent(item.id); // L5
    const request = nextLiked ? api.post(`/v1/gallery/items/${encodedId}/like`) : api.delete(`/v1/gallery/items/${encodedId}/like`);
    request
      .then((r) => updateItemLocally(item.id, { liked_by_me: nextLiked, like_count: r.data.like_count }))
      .catch(() => {
        updateItemLocally(item.id, { liked_by_me: prevLiked, like_count: prevCount }); // true rollback, never left stuck
        toast.error("Couldn't update like");
      })
      .finally(() => {
        pendingLikeIdsRef.current.delete(item.id);
        setPendingLikeIds((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
      });
  }, [updateItemLocally]);

  const openLightbox = (item) => setLightboxItemId(item.id);
  const closeLightbox = () => setLightboxItemId(null);
  const lightboxItem = items.find((it) => it.id === lightboxItemId) || (fallbackItem && fallbackItem.id === lightboxItemId ? fallbackItem : null);

  return (
    <div className="space-y-6" data-testid="gallery-page">
      <div>
        <h1 data-testid="gallery-heading">
          <span className="inline-block bg-brand text-ink font-editorial uppercase tracking-tight text-2xl sm:text-3xl px-3.5 py-1.5 rounded-md">
            The Gallery
          </span>
        </h1>
        <p className="mt-2.5 text-sm text-ink-soft">Work from the network. Films and behind-the-scenes from NJM Build members.</p>
      </div>

      <GallerySyncPanel />

      {deepLinkNotFound && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-paper px-4 py-3" data-testid="gallery-deep-link-not-found">
          <p className="text-sm text-ink-soft">This item is no longer available.</p>
          <button type="button" onClick={() => setDeepLinkNotFound(false)} data-testid="gallery-deep-link-not-found-dismiss"
            className="text-[13px] font-medium text-ink-soft hover:text-ink transition-colors">
            Dismiss
          </button>
        </div>
      )}

      <div className="inline-flex rounded-full border border-hairline bg-paper p-0.5" data-testid="gallery-type-tabs">
        {TYPE_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setType(t.key)} data-testid={`gallery-type-tab-${t.key}`}
            className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors ${type === t.key ? "bg-ink text-white" : "text-ink-soft hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="columns-2 lg:columns-3 min-[1440px]:columns-4 gap-4" data-testid="gallery-loading-skeleton">
          {SKELETON_RATIOS.map((ratio, i) => <SkeletonCard key={i} ratio={ratio} />)}
        </div>
      ) : error ? (
        <div className="py-20 text-center" data-testid="gallery-error-state">
          <div className="font-editorial text-lg text-ink">Couldn't load the Gallery.</div>
          <p className="mt-2 text-sm text-ink-soft">Something went wrong on our end — please try again.</p>
          <button type="button" onClick={() => load(type, null, false)} data-testid="gallery-error-retry-button"
            className="mt-4 rounded-full bg-ink px-4 py-1.5 text-[13px] font-medium text-white hover:bg-ink/85 transition-colors">
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center" data-testid="gallery-empty-state">
          <div className="font-editorial text-lg text-ink">Nothing here yet.</div>
          <p className="mt-2 text-sm text-ink-soft">Check back soon as more NJM Build members share their work.</p>
        </div>
      ) : (
        <>
          <div className="columns-2 lg:columns-3 min-[1440px]:columns-4 gap-4" data-testid="gallery-masonry-grid">
            {items.map((item) => (
              <GalleryCard
                key={item.id}
                item={item}
                onOpenLightbox={openLightbox}
                onToggleLike={toggleLike}
                likePending={pendingLikeIds.has(item.id)}
              />
            ))}
          </div>
          <div ref={sentinelRef} data-testid="gallery-infinite-scroll-sentinel" className="h-4" />
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 py-6 text-[13px] font-medium text-faint" data-testid="gallery-loading-more">
              <span className="h-4 w-4 rounded-full border-2 border-ink border-r-hairline border-b-hairline animate-spin" />
              Loading more work
            </div>
          )}
        </>
      )}

      <GalleryLightbox
        item={lightboxItem}
        onClose={closeLightbox}
        onToggleLike={toggleLike}
        likePending={lightboxItem ? pendingLikeIds.has(lightboxItem.id) : false}
      />
    </div>
  );
}
